import { Skeleton } from "@mui/material";
import {
  Bot,
  Database,
  type LucideIcon,
  Plus,
  Route,
  Shapes,
} from "lucide-react";
import * as React from "react";

import CopyToClipboardButton from "../CopyToClipboardButton";

import styles from "./EntityHeader.module.css";
import {
  HeaderDescription,
  type EntityHeaderDescriptionProps,
} from "./HeaderDescription";
import {
  AddTagPopover,
  HeaderTags,
  type EntityHeaderTagsProps,
} from "./HeaderTags";

export type EntityHeaderType = "dataset" | "session" | "device" | "collection";

const TYPES: Record<EntityHeaderType, { label: string; icon: LucideIcon }> = {
  dataset: { label: "Dataset", icon: Database },
  session: { label: "Session", icon: Route },
  device: { label: "Device", icon: Bot },
  collection: { label: "Collection", icon: Shapes },
};

/** One item of the meta line: a 14px icon, then the value. */
export interface EntityHeaderFact {
  /** Unique within the meta line; React's key for the item. */
  key: string;
  /** A lucide or entity icon, drawn 14px and faded, before the label. */
  icon?: React.ReactNode;
  /**
   * Written before the value, only where the value needs one to read
   * ("Created", "Last session").
   */
  label?: string;
  /** The fact itself: text, a date or a link. */
  value: React.ReactNode;
}

export interface EntityHeaderProps {
  /** Which kind of record: picks the icon, which is labelled with it. */
  type: EntityHeaderType;
  /**
   * The record's name: an `InlineEdit` with `textStyle="title"`, or an
   * {@link EntityName} where it can't be edited.
   */
  name: React.ReactNode;
  /**
   * The name is a system ID (a session still titled by its ID): monospace,
   * with a copy button after it. Not for a device, whose ID is its name.
   */
  nameIsId?: boolean;
  /** After the name: a session's status. */
  badges?: React.ReactNode;
  /** At the far end of the title row: a session's Mark complete button. */
  actions?: React.ReactNode;
  /** Left out for records that have no description field, as a device. */
  description?: EntityHeaderDescriptionProps;
  /** The record's tags; left out for records that have none. */
  tags?: EntityHeaderTagsProps;
  /**
   * Ends the meta line, unlabelled, in monospace with a copy button. Left out
   * where the page's breadcrumb already shows the ID, as a dataset's or a
   * session's.
   */
  id?: string;
  /** The meta line, in order, before the ID. */
  meta?: EntityHeaderFact[];
  /**
   * The record is still loading: the header is a pulsing bar for the name
   * and one for the rest, in place of all of it, until everything can show
   * at once.
   */
  loading?: boolean;
}

/**
 * The header of a resource page (session, device, collection, dataset), on
 * the page itself, with no card: the title row (icon, name, badges, then the
 * page's actions), one icon-led meta line, the description, then the tags.
 * Each keeps its row whether filled or not: a missing description is a quiet
 * "+ Add description" in its place, and missing tags a "+ Add tag" in
 * theirs, both on one line when both are missing.
 */
export function EntityHeader({
  type,
  name,
  nameIsId = false,
  badges,
  actions,
  description,
  tags,
  id,
  meta = [],
  loading = false,
}: EntityHeaderProps) {
  const [editingDescription, setEditingDescription] = React.useState(false);
  const { label, icon: Icon } = TYPES[type];
  if (loading) {
    return (
      <header
        className={styles.header}
        aria-busy="true"
        aria-label={`Loading ${label.toLowerCase()}`}
      >
        <Skeleton variant="text" className={styles.nameSkeleton} />
        <Skeleton variant="text" className={styles.lineSkeleton} />
      </header>
    );
  }
  const hasDescription = Boolean(description?.value);
  const hasTags = (tags?.value.length ?? 0) > 0;
  const canAddDescription = description?.canEdit ?? false;
  const canAddTag = tags?.canAdd ?? false;
  const showsId = id !== undefined && !nameIsId;
  const items: EntityHeaderFact[] = showsId
    ? [
        ...meta,
        {
          key: "id",
          value: (
            <span className={styles.idValue}>
              <span className="sr-only">ID: </span>
              <span className={styles.idText}>{id}</span>
              <CopyToClipboardButton
                text={id}
                ariaLabel={`Copy ${type} ID`}
                className={styles.copy}
              />
            </span>
          ),
        },
      ]
    : meta;

  const addDescription =
    !hasDescription && !editingDescription && canAddDescription;
  const addTag = tags !== undefined && !hasTags && canAddTag;
  const addTagLink = addTag && (
    <AddTagPopover
      onAdd={tags.onAdd}
      trigger={
        <button type="button" className={styles.quietLink}>
          <Plus aria-hidden className={styles.plusIcon} />
          Add tag
        </button>
      }
    />
  );

  return (
    <header className={styles.header}>
      <div className={styles.titleRow}>
        <div className={styles.titleStart}>
          <Icon
            size={18}
            strokeWidth={1.75}
            role="img"
            aria-label={label}
            className={styles.typeIcon}
          />
          <div className={nameIsId ? styles.nameId : styles.name}>
            {name}
            {nameIsId && typeof id === "string" && (
              <CopyToClipboardButton
                text={id}
                ariaLabel={`Copy ${type} ID`}
                className={styles.copy}
              />
            )}
          </div>
          {badges && <div className={styles.badges}>{badges}</div>}
        </div>
        {actions && <div className={styles.titleEnd}>{actions}</div>}
      </div>

      {items.length > 0 && (
        <ul className={styles.meta} aria-label={`${label} details`}>
          {items.map((item, i) => (
            <li key={item.key} className={styles.metaItem}>
              {i > 0 && (
                <span aria-hidden className={styles.dot}>
                  ·
                </span>
              )}
              {item.icon && (
                <span aria-hidden className={styles.metaIcon}>
                  {item.icon}
                </span>
              )}
              {item.label && (
                <span className={styles.metaLabel}>{item.label}</span>
              )}
              <span className={styles.metaValue}>{item.value}</span>
            </li>
          ))}
        </ul>
      )}

      {description && (hasDescription || editingDescription) && (
        <div className={styles.descriptionRow}>
          <HeaderDescription
            {...description}
            editing={editingDescription}
            onEditingChange={setEditingDescription}
          />
        </div>
      )}

      {/* With no description and no tags, both are offered on one line. */}
      {addDescription && (
        <div className={styles.addRow}>
          <button
            type="button"
            className={styles.quietLink}
            onClick={() => setEditingDescription(true)}
          >
            <Plus aria-hidden className={styles.plusIcon} />
            Add description
          </button>
          {addTagLink && (
            <>
              <span aria-hidden className={styles.dot}>
                ·
              </span>
              {addTagLink}
            </>
          )}
        </div>
      )}

      {tags && hasTags && (
        <div className={styles.tagRow}>
          <HeaderTags {...tags} />
        </div>
      )}

      {addTagLink && !addDescription && (
        <div className={styles.addRow}>{addTagLink}</div>
      )}
    </header>
  );
}

/**
 * A record name that can't be edited, styled like the editable ones: a
 * device's ID, or a session's ID while the session loads.
 */
export function EntityName({ children }: { children: React.ReactNode }) {
  return <h1 className={styles.entityName}>{children}</h1>;
}
